// src/components/.../ReviewProductsPopup.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import {
  Button,
  Table,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import {
  IconCirclePlus,
  IconRefresh,
  IconCopy,
  IconAlertTriangle,
  IconBuildingStore,
  IconChevronRight,
  IconRocket,
  IconArrowRight,
  IconInbox,
  IconX,
  IconBox,
  IconFileExport,
} from "@tabler/icons-react";
import usePostRequest from "../../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../../constant/_constant";
import DynamicPagination from "../../../components/DynamicPagination ";
import ImportProductsPopup from "./ImportProductsPopup";
import { toast } from "react-toastify";
import axiosInstance from "../../../api/axiosInstance";
import moment from "moment";

const C = {
  primary: "#FE6338",
  primarySoft: "#FFF1EC",
  ink: "#141821",
  sub: "#6B7280",
  line: "#ECEEF2",
  panel: "#F7F8FA",
};

const CATS = {
  kit: {
    label: "Kits",
    desc: "Kits and their component products",
    c: "#0EA5A5",
    bg: "#E6FAFA",
    Icon: IconBox, // add IconBox to the tabler import
  },
  new: {
    label: "New Additions",
    desc: "Products not in the system yet",
    c: "#0E9F6E",
    bg: "#E7F8F1",
    Icon: IconCirclePlus,
  },
  update: {
    label: "Updates",
    desc: "Existing products with changes",
    c: "#2A72F8",
    bg: "#EAF1FF",
    Icon: IconRefresh,
  },
  duplicate: {
    label: "Duplicates",
    desc: "SKUs matching multiple records",
    c: "#E0820C",
    bg: "#FEF5E7",
    Icon: IconCopy,
  },
  missing: {
    label: "Missing",
    desc: "Referenced but not found",
    c: "#E5484D",
    bg: "#FDECEC",
    Icon: IconAlertTriangle,
  },
  new_vendor: {
    label: "New Vendors",
    desc: "Vendors to be created",
    c: "#8B5CF6",
    bg: "#F1ECFE",
    Icon: IconBuildingStore,
  },
};

function ReviewProductsPopup({ show, setShow, summaryData, onApprove }) {
  const summary = summaryData?.summary || {};
  const batch_no = summaryData?.batch_no;

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedRows, setSelectedRows] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [uploadingFix, setUploadingFix] = useState(false);
  const fixFileInputRef = useRef(null);

  const { data: rowsData, postData: fetchBatchRows } = usePostRequest(
    API_ENDPOINTS.GET_IMPORT_BATCH_ROWS,
  );

  const { isLoading: exportings, postData: exportMissing } = usePostRequest(
    API_ENDPOINTS.EXPORT_CSV,
  );

  const handleExportMissing = async () => {
    setExporting(true);
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.EXPORT_CSV,
        { batch_no },
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `missing_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleFixFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(csv|xlsx|xls)$/i.test(file.name)) {
      toast.error("Only CSV or Excel (.xlsx, .xls) files are supported.");
      e.target.value = "";
      return;
    }
    setUploadingFix(true);
    try {
      const formData = new FormData();
      formData.append("batch_no", batch_no);
      formData.append("file", file);
      const res = await axiosInstance.post(API_ENDPOINTS.IMPORT_CSV, formData);
      toast.success(res?.data?.message || "File uploaded successfully.");
      setSelectedRows([]);
      setPage(1);
      setActiveStatus("kit"); // switch to kits; effect refetches
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(
        error?.response?.data?.message || "Upload failed. Please try again.",
      );
    } finally {
      setUploadingFix(false);
      e.target.value = "";
    }
  };

  const isKitMode = summary?.total_kits !== undefined;

  const boxes = isKitMode
    ? [
        { status: "kit", value: summary.total_kits ?? 0 },
        { status: "missing", value: summary.missing ?? 0 },
        { status: "new_vendor", value: summary.new_vendor ?? 0 },
      ]
    : [
        { status: "new", value: summary.new ?? 0 },
        { status: "update", value: summary.update ?? 0 },
        { status: "duplicate", value: summary.duplicate ?? 0 },
        { status: "missing", value: summary.missing ?? 0 },
        { status: "new_vendor", value: summary.new_vendor ?? 0 },
      ];

  const rows = rowsData?.data?.rows || [];
  const currentPageIds = rows.map((r) => r.id);
  const allCurrentSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedRows.includes(id));

  const toggleRow = (id) =>
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );

  const toggleSelectAll = () => {
    if (allCurrentSelected)
      setSelectedRows((prev) =>
        prev.filter((id) => !currentPageIds.includes(id)),
      );
    else
      setSelectedRows((prev) =>
        Array.from(new Set([...prev, ...currentPageIds])),
      );
  };

  // auto-select first non-empty category when opened
  useEffect(() => {
    if (show && !activeStatus) {
      const first = boxes.find((b) => b.value > 0);
      if (first) setActiveStatus(first.status);
    }
  }, [show]); // eslint-disable-line

  useEffect(() => {
    if (!activeStatus || !batch_no) return;
    fetchBatchRows({ batch_no, page, limit, status: activeStatus });
  }, [activeStatus, page, limit, batch_no]);

  const handleSelectCat = (status) => {
    if (status === activeStatus) return;
    setActiveStatus(status);
    setPage(1);
  };

  const handleClose = () => {
    setShow(false);
    setActiveStatus(null);
    setPage(1);
    setSelectedRows([]);
  };

  const handlePageChange = (p) => setPage(p);
  const handleLimitChange = (l) => {
    setLimit(l);
    setPage(1);
  };

  const KIT_LABELS = {
    new: { text: "New", c: "#0E9F6E", bg: "#E7F8F1" },
    new_in_kit: { text: "New in Kit", c: "#0E9F6E", bg: "#E7F8F1" },
    existing_in_kit: { text: "Existing in Kit", c: "#2A72F8", bg: "#EAF1FF" },
    duplicate: { text: "Duplicate", c: "#E0820C", bg: "#FEF5E7" },
  };

  const LabelPill = ({ label }) => {
    const s = KIT_LABELS[label] || {
      text: label || "—",
      c: C.sub,
      bg: C.panel,
    };
    return (
      <span
        className="px-2 py-1"
        style={{
          background: s.bg,
          color: s.c,
          borderRadius: 20,
          fontSize: 11.5,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {s.text}
      </span>
    );
  };

  const KIT_STATUS = {
    new: { text: "New Kit", c: "#0E9F6E", bg: "#E7F8F1" },
    update: { text: "Existing Kit (Update)", c: "#2A72F8", bg: "#EAF1FF" },
    identical: { text: "No Changes", c: "#6B7280", bg: "#F0F1F3" },
    duplicate: { text: "Duplicate", c: "#E0820C", bg: "#FEF5E7" },
    blocked: { text: "Blocked", c: "#E5484D", bg: "#FDECEC" },
  };

  const KitStatusPill = ({ status }) => {
    const s = KIT_STATUS[status];
    if (!s) return null;
    return (
      <span
        className="px-2 py-1"
        style={{
          background: s.bg,
          color: s.c,
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {s.text}
      </span>
    );
  };

  const VendorChangedPill = () => (
    <span
      className="d-inline-flex align-items-center gap-1 px-2 py-1"
      style={{
        background: "#FEF5E7",
        color: "#E0820C",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <IconRefresh size={12} /> Vendor changed
    </span>
  );
  const truncate = (v) =>
    v ? (v.length > 100 ? v.slice(0, 100) + "..." : v) : "";

  const Diff = ({ from, to }) => (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span
        className="px-2 py-1"
        style={{
          background: "#FDECEC",
          color: "#C22",
          borderRadius: 6,
          fontSize: 12.5,
          textDecoration: "line-through",
        }}
      >
        {from ?? "N/A"}
      </span>
      <IconArrowRight size={13} color={C.sub} />
      <span
        className="px-2 py-1"
        style={{
          background: "#E7F8F1",
          color: "#087A4F",
          borderRadius: 6,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        {to ?? "N/A"}
      </span>
    </div>
  );

  const renderDiff = (
    product,
    field,
    currentValue,
    { isVendor = false } = {},
  ) => {
    const change = product?.changes?.[isVendor ? "vendor" : field];
    if (!change)
      return <span style={{ color: C.ink }}>{currentValue ?? ""}</span>;
    return (
      <Diff
        from={isVendor ? change.from_name : change.from}
        to={isVendor ? change.to_name : change.to}
      />
    );
  };

  const fmtPrice = (v) =>
    v === null || v === undefined || v === ""
      ? "N/A"
      : Number(v).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const renderPriceDiff = (product, field, currentValue) => {
    const ch = product?.changes?.[field];
    if (!ch)
      return <span style={{ color: C.ink }}>{fmtPrice(currentValue)}</span>;
    return <Diff from={fmtPrice(ch.from)} to={fmtPrice(ch.to)} />;
  };

  // Add helper near truncate
  const DescCell = ({ value }) => {
    const text = value ?? "";
    if (!text) return "";
    const short = text.length > 20 ? text.slice(0, 20) + "..." : text;
    if (text.length <= 20) return <span>{short}</span>;
    return (
      <OverlayTrigger placement="bottom" overlay={<Tooltip>{text}</Tooltip>}>
        <span style={{ cursor: "help" }}>{short}</span>
      </OverlayTrigger>
    );
  };

  // Diff variant that truncates + tooltips each side
  const DescDiff = ({ from, to }) => (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span
        className="px-2 py-1"
        style={{
          background: "#FDECEC",
          color: "#C22",
          borderRadius: 6,
          fontSize: 12.5,
          textDecoration: "line-through",
        }}
      >
        <DescCell value={from} />
      </span>
      <IconArrowRight size={13} color={C.sub} />
      <span
        className="px-2 py-1"
        style={{
          background: "#E7F8F1",
          color: "#087A4F",
          borderRadius: 6,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        <DescCell value={to} />
      </span>
    </div>
  );

  const TypePill = ({ type, kit }) => {
    if (!type) return null;
    const map = {
      kit: { c: "#8B5CF6", bg: "#F1ECFE" },
      "non-inventory": { c: "#E0820C", bg: "#FEF5E7" },
      inventory: { c: "#2A72F8", bg: "#EAF1FF" },
    };
    const s = map[type] || { c: C.sub, bg: "#F0F1F3" };
    const label =
      ({ kit: "Kit", "non-inventory": "Non-Inventory", inventory: "Inventory" }[
        type
      ] || "") + (kit ? ` (${kit})` : "");
    return (
      <span
        className="px-2 py-1"
        style={{
          background: s.bg,
          color: s.c,
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    );
  };

  const th = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: C.sub,
    textTransform: "uppercase",
    padding: "12px 16px",
    borderBottom: `1px solid ${C.line}`,
    background: C.panel,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };
  const td = {
    fontSize: 13.5,
    color: C.ink,
    padding: "13px 16px",
    borderBottom: `1px solid ${C.line}`,
    verticalAlign: "middle",
  };

  const activeCat = activeStatus ? CATS[activeStatus] : null;
  const showPricing =
    activeStatus === "update" ||
    activeStatus === "new" ||
    activeStatus === "missing";

  const hasBlockingMissing = isKitMode && (summary?.missing ?? 0) > 0;
  const renderTable = () => {
    if (!activeStatus) return null;

    if (rows.length === 0)
      return (
        <div
          className="d-flex flex-column align-items-center justify-content-center text-center"
          style={{ padding: "70px 20px", color: C.sub }}
        >
          <span
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: C.panel,
            }}
          >
            <IconInbox size={26} color="#B4BAC5" />
          </span>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
            No records here
          </div>
          <div style={{ fontSize: 13 }}>
            This category has no matching products.
          </div>
        </div>
      );

    if (activeStatus === "new_vendor")
      return (
        <div className="d-flex flex-column p-3 gap-2">
          {rows.map((row, i) => (
            <div
              key={i}
              className="d-flex align-items-center gap-2 px-3 py-2"
              style={{
                background: "#fff",
                border: `1px solid ${C.line}`,
                borderRadius: 10,
                fontSize: 13.5,
                fontWeight: 500,
                color: C.ink,
              }}
            >
              <IconBuildingStore size={16} color={CATS.new_vendor.c} />
              {row?.vendor_name}
            </div>
          ))}
        </div>
      );

    if (activeStatus === "kit")
      return (
        <div className="d-flex flex-column p-3 gap-4">
          {(summary?.missing ?? 0) > 0 && (
            <div
              className="d-flex align-items-start gap-2 px-3 py-2"
              style={{
                background: "#FDECEC",
                border: "1px solid #F5B5B7",
                borderRadius: 10,
                fontSize: 13,
                color: "#C0292E",
              }}
            >
              <IconAlertTriangle
                size={17}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                Some products are missing mandatory fields, so this file cannot
                be processed. Please fill up the missing fields in the products
                properly and try again.
              </span>
            </div>
          )}
          {rows.map((p, i) => (
            <div
              key={p.id ?? i}
              style={{
                border: `1px solid ${C.line}`,
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <Table className="mb-0" borderless>
                <thead>
                  <tr>
                    <th style={th}>Status</th>
                    <th style={th}>Vendor</th>
                    <th style={th}>SKU</th>
                    <th style={th}>Product Name</th>
                    <th style={th}>Description</th>
                    {/* <th style={th}>Qty</th> */}
                    <th style={th}>Aspetto Cost ($)</th>
                    <th style={th}>Customer Price ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Parent kit row — distinct styling */}
                  <tr
                    style={{
                      background: `linear-gradient(135deg, ${C.primarySoft}, #FFF)`,
                      borderLeft: `3px solid ${C.primary}`,
                    }}
                  >
                    <td style={{ ...td, fontWeight: 700 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="d-inline-flex align-items-center gap-1">
                          <IconBox size={15} color={C.primary} />
                          <span
                            style={{
                              fontSize: 11,
                              color: C.primary,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          >
                            Kit
                          </span>
                        </span>
                        <KitStatusPill status={p?.status} />
                      </div>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{p?.vendor_name}</td>
                    <td
                      style={{
                        ...td,
                        color: C.primary,
                        fontWeight: 700,
                        fontSize: 14.5,
                      }}
                    >
                      {p?.sku_display_name}
                      {p?.vendor_changed && (
                        <span className="ms-2">
                          <VendorChangedPill />
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 700 }}>
                      {p?.name} <TypePill type={p?.type} kit={p?.kit_type} />
                    </td>
                    <td style={{ ...td, maxWidth: 260 }}>
                      <DescCell value={p?.description} />
                    </td>
                    {/* <td style={td}>—</td> */}
                    <td style={{ ...td, fontWeight: 600 }}>
                      {fmtPrice(p?.aspetto_price)}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {fmtPrice(p?.customer_price)}
                    </td>
                  </tr>

                  {/* Component product rows */}
                  {p?.kits?.map((k) => (
                    <tr key={k.id}>
                      <td style={{ ...td, paddingLeft: 32 }}>
                        <div className="d-flex align-items-center gap-1 flex-wrap">
                          <LabelPill label={k?.label} />
                          {k?.vendor_changed && <VendorChangedPill />}
                        </div>
                      </td>
                      <td style={td}>{k?.vendor_name}</td>
                      <td style={{ ...td, color: C.primary, fontWeight: 600 }}>
                        {k?.sku_display_name}
                      </td>
                      <td style={td}>{k?.name}</td>
                      <td style={{ ...td, maxWidth: 260 }}>
                        <DescCell value={k?.description} />
                      </td>
                      {/* <td style={td}>{k?.quantity}</td> */}
                      <td style={td}>{fmtPrice(k?.aspetto_price)}</td>
                      <td style={td}>{fmtPrice(k?.customer_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      );

    if (activeStatus === "duplicate")
      return (
        <div className="d-flex flex-column p-3 gap-4">
          {rows.map((p, i) => (
            <div
              key={p.id ?? i}
              style={{
                border: `1px solid ${C.line}`,
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              {/* Parent SKU band */}
              <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  background: C.primarySoft,
                  borderBottom: `1px solid ${C.line}`,
                }}
              >
                <IconCopy size={16} color={C.primary} />
                <span
                  style={{
                    fontSize: 12,
                    color: C.sub,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  Importing SKU
                </span>
                <span
                  style={{ fontSize: 14, fontWeight: 700, color: C.primary }}
                >
                  {p?.sku_display_name}
                </span>
                <span style={{ fontSize: 12.5, color: C.sub }}>
                  — matches {p?.duplicate_matches?.length ?? 0} record(s) in CRM
                </span>
              </div>

              {/* Matches table */}
              <Table className="mb-0" borderless>
                <thead>
                  <tr>
                    <th style={th}>Vendor</th>
                    <th style={th}>SKU</th>
                    <th style={th}>Product Name</th>
                    <th style={th}>Description</th>
                    <th style={th}>Sales Description</th>
                    <th style={th}>Aspetto Cost ($)</th>
                    <th style={th}>Customer Price ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {p?.duplicate_matches?.map((m) => (
                    <tr key={m.id}>
                      <td style={td}>{m?.vendor_name}</td>
                      <td style={{ ...td, color: C.primary, fontWeight: 600 }}>
                        {m?.sku_display_name}
                      </td>
                      <td style={td}>{m?.name}</td>
                      <td style={{ ...td, maxWidth: 260 }}>
                        <DescCell value={m?.description} />
                      </td>
                      <td style={{ ...td, maxWidth: 260 }}>
                        <DescCell value={m?.description_sales} />
                      </td>
                      <td style={td}>{fmtPrice(m?.aspetto_price)}</td>
                      <td style={td}>{fmtPrice(m?.customer_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      );

    // if (activeStatus === "duplicate")
    //   return (
    //     <Table className="mb-0" borderless>
    //       <thead>
    //         <tr>
    //           <th style={th}>SKU</th>
    //           <th style={th}>Duplicate Matches</th>
    //         </tr>
    //       </thead>
    //       <tbody>
    //         {rows.map((p, i) => (
    //           <tr key={p.id ?? i}>
    //             <td style={{ ...td, color: C.primary, fontWeight: 600 }}>
    //               {p?.sku_display_name}
    //             </td>
    //             <td style={td}>
    //               <div className="d-flex flex-column gap-1">
    //                 {p?.duplicate_matches?.map((m) => (
    //                   <div
    //                     key={m.id}
    //                     className="px-2 py-1"
    //                     style={{
    //                       background: C.panel,
    //                       border: `1px solid ${C.line}`,
    //                       borderRadius: 8,
    //                       fontSize: 13,
    //                     }}
    //                   >
    //                     <span style={{ color: C.primary, fontWeight: 600 }}>
    //                       {m.sku_display_name}
    //                     </span>{" "}
    //                     — {m.name}
    //                   </div>
    //                 ))}
    //               </div>
    //             </td>
    //           </tr>
    //         ))}
    //       </tbody>
    //     </Table>
    //   );

    return (
      <Table className="mb-0" borderless>
        <thead>
          <tr>
            {activeStatus === "update" && (
              <th style={{ ...th, width: 46 }}>
                <Form.Check
                  type="checkbox"
                  checked={allCurrentSelected}
                  onChange={toggleSelectAll}
                />
              </th>
            )}
            <th style={th}>Vendor</th>
            <th style={th}>SKU</th>
            <th style={th}>Product Name</th>
            <th style={th}>Type</th>
            <th style={th}>Description</th>
            <th style={th}>Sales Description</th>
            {showPricing && (
              <>
                <th style={th}>Aspetto Cost ($)</th>
                <th style={th}>Customer Price ($)</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={p.id ?? i}>
              {activeStatus === "update" && (
                <td style={td}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedRows.includes(p.id)}
                    onChange={() => toggleRow(p.id)}
                  />
                </td>
              )}
              <td style={td}>
                {activeStatus === "update"
                  ? renderDiff(p, "vendor", p?.vendor_name, { isVendor: true })
                  : p?.vendor_name}
              </td>
              <td style={{ ...td, color: C.primary, fontWeight: 600 }}>
                {p?.sku_display_name}
              </td>
              <td style={td}>
                {activeStatus === "update"
                  ? renderDiff(p, "name", p?.name)
                  : p?.name}
              </td>
              <td style={td}>
                <TypePill type={p?.type} kit={p?.kit_type} />
              </td>
              <td style={{ ...td, maxWidth: 260 }}>
                {activeStatus === "update" ? (
                  (() => {
                    const ch = p?.changes?.description;
                    if (!ch) return <DescCell value={p?.description} />;
                    return <DescDiff from={ch.from} to={ch.to} />;
                  })()
                ) : (
                  <DescCell value={p?.description} />
                )}
              </td>
              <td style={{ ...td, maxWidth: 260 }}>
                {activeStatus === "update" ? (
                  (() => {
                    const ch = p?.changes?.description_sales;
                    if (!ch) return <DescCell value={p?.description_sales} />;
                    return <DescDiff from={ch.from} to={ch.to} />;
                  })()
                ) : (
                  <DescCell value={p?.description_sales} />
                )}
              </td>
              {showPricing && (
                <>
                  <td style={td}>
                    {renderPriceDiff(p, "aspetto_price", p?.aspetto_price)}
                  </td>
                  <td style={td}>
                    {renderPriceDiff(p, "customer_price", p?.customer_price)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <ImportProductsPopup
        show={isImportOpen}
        onHide={() => setIsImportOpen(false)}
        batchNo={batch_no}
        isKitMode={isKitMode}
        newTotal={summary.new ?? 0}
        updateTotal={summary.update ?? 0}
        selectedUpdateRows={selectedRows}
        onImportSuccess={(res) => {
          onApprove?.(res);
          handleClose();
        }}
      />

      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="modal-90w"
        size="xl"
        centered
        contentClassName="border-0 overflow-hidden"
      >
        <div
          style={{ borderRadius: 20, overflow: "hidden", background: "#fff" }}
        >
          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between px-4"
            style={{ paddingTop: 20, paddingBottom: 18 }}
          >
            <div className="d-flex align-items-center gap-3">
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: C.primarySoft,
                  color: C.primary,
                }}
              >
                <IconRocket size={22} stroke={1.9} />
              </span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.ink }}>
                  Preview &amp; Validate
                </div>
                <div style={{ fontSize: 13, color: C.sub }}>
                  Review each category before importing
                </div>
              </div>
            </div>
            <IconX
              size={20}
              role="button"
              onClick={handleClose}
              style={{ color: "#98A2B3", cursor: "pointer" }}
            />
          </div>

          <div style={{ height: 1, background: C.line }} />

          {/* Body: sidebar + content */}
          <div className="d-flex" style={{ minHeight: 440, maxHeight: "62vh" }}>
            {/* Sidebar */}
            <div
              style={{
                width: 288,
                flexShrink: 0,
                borderRight: `1px solid ${C.line}`,
                background: C.panel,
                overflowY: "auto",
                padding: 12,
              }}
            >
              {boxes.map((b) => {
                const cat = CATS[b.status];
                const Ic = cat.Icon;
                const active = activeStatus === b.status;
                const disabled = b.value === 0;
                return (
                  <div
                    key={b.status}
                    role="button"
                    onClick={() => !disabled && handleSelectCat(b.status)}
                    className="d-flex align-items-center gap-3 px-2 py-2 mb-1 position-relative"
                    style={{
                      borderRadius: 12,
                      cursor: disabled ? "default" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      background: active ? "#fff" : "transparent",
                      boxShadow: active
                        ? "0 2px 10px rgba(20,24,33,.06)"
                        : "none",
                      border: `1px solid ${active ? C.line : "transparent"}`,
                      transition: "all .15s ease",
                    }}
                  >
                    {active && (
                      <span
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 10,
                          bottom: 10,
                          width: 3,
                          borderRadius: 3,
                          background: cat.c,
                        }}
                      />
                    )}
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: cat.bg,
                        color: cat.c,
                        flexShrink: 0,
                      }}
                    >
                      <Ic size={20} stroke={1.9} />
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: C.ink,
                          }}
                        >
                          {cat.label}
                        </span>
                        <span
                          className="px-2"
                          style={{
                            background: active ? cat.bg : "#fff",
                            color: active ? cat.c : C.sub,
                            border: `1px solid ${C.line}`,
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 700,
                            minWidth: 26,
                            textAlign: "center",
                          }}
                        >
                          {b.value}
                        </span>
                      </div>
                      <div
                        className="text-truncate"
                        style={{ fontSize: 11.5, color: C.sub }}
                      >
                        {cat.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Content */}
            <div
              className="flex-grow-1 d-flex flex-column"
              style={{ minWidth: 0 }}
            >
              {activeCat && (
                <div
                  className="d-flex align-items-center gap-2 px-4"
                  style={{
                    paddingTop: 16,
                    paddingBottom: 14,
                    borderBottom: `1px solid ${C.line}`,
                  }}
                >
                  {/* <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 30, height: 30, borderRadius: 8, background: activeCat.bg, color: activeCat.c }}>
                    <activeCat.Icon size={17} stroke={2} />
                  </span> */}
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                    {activeCat.label}
                  </span>
                  <IconChevronRight size={14} color="#C7CDD6" />
                  <span style={{ fontSize: 13, color: C.sub }}>
                    {activeCat.desc}
                  </span>
                </div>
              )}

              {activeStatus === "missing" && !isKitMode && rows.length > 0 && (
                <div className="d-flex justify-content-end gap-2 px-4 pt-3">
                  <Button
                    onClick={handleExportMissing}
                    disabled={exportings}
                    className="d-flex align-items-center gap-2 fw-semibold"
                    style={{
                      borderRadius: 10,
                      background: "#fff",
                      border: `1.5px solid ${C.line}`,
                      color: C.primary,
                      padding: "7px 16px",
                      fontSize: 13,
                    }}
                  >
                    {exportings ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <IconFileExport size={16} /> Export CSV
                      </>
                    )}
                  </Button>
                  {/* <Button
                    onClick={() => fixFileInputRef.current?.click()}
                    disabled={uploadingFix}
                    className="d-flex align-items-center gap-2 fw-semibold border-0"
                    style={{
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${C.primary}, #FF7A54)`,
                      color: "#fff",
                      padding: "7px 16px",
                      fontSize: 13,
                    }}
                  >
                    {uploadingFix ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <IconRocket size={15} /> Upload
                      </>
                    )}
                  </Button>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    hidden
                    ref={fixFileInputRef}
                    onChange={handleFixFileSelect}
                  /> */}
                </div>
              )}

              <div className="flex-grow-1" style={{ overflow: "auto" }}>
                {renderTable()}
              </div>

              {activeStatus && rows.length > 0 && (
                <div
                  className="px-3 py-2"
                  style={{ borderTop: `1px solid ${C.line}` }}
                >
                  <DynamicPagination
                    totalPages={Math.ceil(
                      (rowsData?.data?.total || 0) /
                        (rowsData?.data?.limit || limit),
                    )}
                    currentPage={page}
                    onPageChange={handlePageChange}
                    limitPerPage={limit}
                    onLimitChange={handleLimitChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div style={{ height: 1, background: C.line }} />

          {/* Footer */}
          <div
            className="d-flex align-items-center justify-content-between px-4"
            style={{ paddingTop: 16, paddingBottom: 18, background: "#fff" }}
          >
            <div style={{ fontSize: 12.5, color: C.sub }}>
              {selectedRows.length > 0
                ? `${selectedRows.length} update${selectedRows.length > 1 ? "s" : ""} selected`
                : "Selected updates will be applied on import"}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button
                onClick={handleClose}
                className="fw-semibold"
                style={{
                  borderRadius: 12,
                  background: "#fff",
                  border: `1.5px solid ${C.line}`,
                  color: C.sub,
                  padding: "9px 22px",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => setIsImportOpen(true)}
                disabled={hasBlockingMissing}
                className="d-flex align-items-center gap-2 fw-semibold border-0"
                style={{
                  borderRadius: 12,
                  background: hasBlockingMissing
                    ? "#FEB59D"
                    : `linear-gradient(135deg, ${C.primary}, #FF7A54)`,
                  color: "#fff",
                  padding: "9px 22px",
                  boxShadow: hasBlockingMissing
                    ? "none"
                    : "0 6px 16px rgba(254,99,56,.35)",
                  cursor: hasBlockingMissing ? "not-allowed" : "pointer",
                }}
              >
                <IconRocket size={17} /> Approve &amp; Execute Import
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default ReviewProductsPopup;
